import { batchImportRowMatch, collectApplianceMatches, normalizeSerial } from "@/lib/scanNavigation";
import i18n from "i18next";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  X,
  RefreshCw,
  ImagePlus,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  FileDown,
} from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { useCompanyApplianceCategories } from "@/hooks/useCompanyApplianceCategories";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { HarvestRecommendationItem } from "@/hooks/useHarvestRecommendations";
import { resolveScannedDryingTechnology, type DryingTechnology } from "@/lib/dryerTechnology";
import { existingAppliancePatch, inferProductGroup, matchCompanyCategory } from "@/lib/batchImportCategory";
import { adminFunctionError } from "@/lib/adminFunctionError";

type RowStatus = "pending" | "uploading" | "scanning" | "enriching" | "done" | "failed";
type RecStatus = "idle" | "loading" | "done" | "skipped" | "error";
type WorkflowKind = "repair" | "refurbish" | "harvest";

interface Row {
  id: string;
  file: File;
  previewUrl: string;
  imageDataUrl: string;
  status: RowStatus;
  error?: string;
  typeShieldUrl?: string;
  brand: string;
  modelNumber: string;
  serialNumber: string;
  productNumber?: string;
  productType?: string;
  plateType?: string;
  fdNumber?: string;
  zNr?: string;
  plateVersion?: string;
  productionNumber?: string;
  category: string;
  eprelReference?: string;
  energyClass?: string;
  productImageUrl?: string;
  eprelData?: any;
  eprelClassification?: import("@/lib/eprelClassification").ApplianceClassification;
  /** FIX-289: drying technology read from the type shield by ocr-scan. */
  scanDryerTechnology?: DryingTechnology | null;
  existingApplianceId?: string;
  include: boolean;
  workflow: WorkflowKind;
  selected: boolean;
  recommendations?: HarvestRecommendationItem[];
  recommendationsStatus?: RecStatus;
  recommendationsError?: string;
  recommendationsMatchedOn?: { category: string; technology: string; brand: string };
}


const MAX_FILES = 25;
const CONCURRENCY = 3;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function runWithLimit<T>(items: T[], limit: number, fn: (item: T, i: number) => Promise<void>) {
  let idx = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (true) {
      const i = idx++;
      if (i >= items.length) return;
      await fn(items[i], i);
    }
  });
  await Promise.all(workers);
}


// Map the EPREL applianceCategory enum to the EXACT category label used
// inside the recommendation workbook's "Parts Register" sheet. The workbook
// filter compares with raw equality, so this MUST return the workbook's
// vocabulary — not a company-configured category name.
function eprelCategoryToWorkbookCategory(
  applianceCategory: string | undefined
): string {
  switch (applianceCategory) {
    case "WASHING_MACHINE":
      return "Washing Machine";
    case "TUMBLE_DRYER":
      return "Tumble Dryer";
    case "DISHWASHER":
      return "Dishwasher";
    default:
      return "";
  }
}

// Fallback when EPREL classification is missing: derive the workbook category
// from a free-text label (OCR product type, company-configured category, …).
function freeTextToWorkbookCategory(value: string | undefined): string {
  const v = String(value || "").toLowerCase();
  if (!v) return "";
  if (v.includes("dish")) return "Dishwasher";
  if (v.includes("dryer") || v.includes("tumble") || v.includes("tumbler")) return "Tumble Dryer";
  if (v.includes("wash")) return "Washing Machine";
  return "";
}


function recommendationCategoryFamily(value: string | undefined): "wash" | "dish" | "dryer" | "other" {
  const v = String(value || "").toLowerCase();
  if (v.includes("dish")) return "dish";
  if (v.includes("dryer") || v.includes("tumble") || v.includes("tumbler")) return "dryer";
  if (v.includes("wash") || v.includes("washer")) return "wash";
  return "other";
}

const RECOMMENDATION_BRAND_ALIASES: Record<string, string> = {
  aeg: "AEG",
  beko: "Beko",
  bosch: "Bosch",
  bsh: "Bosch",
  candy: "Candy",
  constructa: "Constructa",
  electrolux: "Electrolux",
  gaggenau: "Gaggenau",
  gorenje: "Gorenje",
  haier: "Haier",
  hisense: "Hisense",
  hoover: "Hoover",
  hotpoint: "Hotpoint",
  hotpointariston: "Hotpoint Ariston",
  ikea: "IKEA",
  indesit: "Indesit",
  lg: "LG",
  liebherr: "Liebherr",
  miele: "Miele",
  neff: "Neff",
  ok: "OK",
  samsung: "Samsung",
  siemens: "Siemens",
  smeg: "Smeg",
  whirlpool: "Whirlpool",
  zanussi: "Zanussi",
};

function normalizeRecommendationBrand(brand: string | undefined): string {
  const compact = String(brand || "")
    .normalize("NFKC")
    .replace(/[®™]/g, "")
    .replace(/&/g, " and ")
    .replace(
      /\b(gmbh|b\.?v\.?|n\.?v\.?|ag|kg|ltd\.?|limited|inc\.?|corp\.?|corporation|company|co\.?)\b/gi,
      " "
    )
    .replace(/\b(hausgeraete|hausgeräte|home appliances?|electrogeraete|electrogeräte|elektrogeraete|elektrogeräte)\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!compact) return "";

  const key = compact.toLowerCase().replace(/[^a-z0-9]+/g, "");
  const aliased = RECOMMENDATION_BRAND_ALIASES[key];
  if (aliased) return aliased;

  return compact
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

type Classification = import("@/lib/eprelClassification").ApplianceClassification;

const DRYER_TECH_LABEL: Record<string, string> = {
  heat_pump: "Heat pump",
  condenser: "Condenser",
  vented: "Vented",
};
const DW_INSTALL_LABEL: Record<string, string> = {
  freestanding: "Free-standing",
  fully_integrated: "Fully integrated",
  semi_integrated: "Semi-integrated",
};
const DW_WIDTH_LABEL: Record<string, string> = {
  standard_60cm: "60 cm",
  slim_45cm: "45 cm",
};
const APPLIANCE_CLASS_LABEL: Record<string, string> = {
  WASHING_MACHINE: "Washing machine",
  TUMBLE_DRYER: "Tumble Dryer",
  DISHWASHER: "Dishwasher",
  UNKNOWN: "",
};

function formatClassification(c: Classification | undefined): string {
  if (!c) return "";
  if (c.applianceCategory === "TUMBLE_DRYER") {
    return c.dryerTechnology ? DRYER_TECH_LABEL[c.dryerTechnology] : "";
  }
  if (c.applianceCategory === "DISHWASHER") {
    const parts = [
      c.dishwasherInstall ? DW_INSTALL_LABEL[c.dishwasherInstall] : "",
      c.dishwasherWidth ? DW_WIDTH_LABEL[c.dishwasherWidth] : "",
      c.dishwasherCapacity ? `${c.dishwasherCapacity} ps` : "",
    ].filter(Boolean);
    return parts.join(" · ");
  }
  return "";
}

/** FIX-289: the type-shield technology, labelled so it can be told apart from EPREL. */
function scanTechnologyLabel(t: DryingTechnology | null | undefined): string {
  return t && DRYER_TECH_LABEL[t] ? `${DRYER_TECH_LABEL[t]} (type shield)` : "";
}

export default function BatchImport() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id: companyId } = useCurrentCompany();
  const { names: categoryNames } = useCompanyApplianceCategories();

  const [rows, setRows] = useState<Row[]>([]);
  const [step, setStep] = useState<"upload" | "review">("upload");
  const [processing, setProcessing] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [skipConfirm, setSkipConfirm] = useState(false);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [exportInfo, setExportInfo] = useState<{ url: string; name: string } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files).filter((f) => ACCEPTED.includes(f.type));
      if (arr.length === 0) {
        toast.error("Only JPG, PNG or WEBP images are accepted.");
        return;
      }
      const slots = MAX_FILES - rows.length;
      if (slots <= 0) {
        toast.error(`Maximum ${MAX_FILES} images per batch.`);
        return;
      }
      const accepted = arr.slice(0, slots);
      if (arr.length > slots) {
        toast.warning(`Only the first ${slots} files were added (limit ${MAX_FILES}).`);
      }
      const newRows: Row[] = await Promise.all(
        accepted.map(async (file) => ({
          id: crypto.randomUUID(),
          file,
          previewUrl: URL.createObjectURL(file),
          imageDataUrl: await fileToDataUrl(file),
          status: "pending" as RowStatus,
          brand: "",
          modelNumber: "",
          serialNumber: "",
          category: "",
          include: true,
          workflow: "harvest" as WorkflowKind,
          selected: false,
          recommendationsStatus: "idle" as RecStatus,
        }))
      );
      setRows((prev) => [...prev, ...newRows]);
    },
    [rows.length]
  );

  const removeRow = (id: string) => {
    setRows((prev) => {
      const r = prev.find((x) => x.id === id);
      if (r) URL.revokeObjectURL(r.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
  };

  const patchRow = (id: string, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const processRow = useCallback(
    async (row: Row) => {
      try {
        patchRow(row.id, { status: "uploading", error: undefined });
        const { data: uploadData, error: uploadErr } = await supabase.functions.invoke(
          "upload-type-shield",
          { body: { imageData: row.imageDataUrl } }
        );
        if (uploadErr) throw new Error(uploadErr.message || "Upload failed");
        const typeShieldUrl: string | undefined = uploadData?.data?.url;

        patchRow(row.id, { status: "scanning", typeShieldUrl });
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;
        if (!accessToken) throw new Error("Session expired — please sign in again.");

        const { data: ocrData, error: ocrErr } = await supabase.functions.invoke("ocr-scan", {
          body: { imageData: row.imageDataUrl },
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        // Show the scan's own message (e.g. "The scan answer could not be read, please retry.").
        if (ocrErr) throw await adminFunctionError(ocrErr);
        const ocr = ocrData?.data || {};
        const brand: string = ocr.brand || "";
        const modelNumber: string = ocr.modelNumber || "";
        const serialNumber: string = ocr.serialNumber || "";
        const productNumber: string | undefined = ocr.productNumber;
        const productType: string | undefined = ocr.productType;
        const plateType: string | undefined = ocr.plateType || undefined;
        const fdNumber: string | undefined = ocr.fdCode || undefined;
        const zNr: string | undefined = ocr.zNr || undefined;
        const plateVersion: string | undefined = ocr.plateVersion || undefined;
        const productionNumber: string | undefined = ocr.productionNumber || undefined;
        // FIX-289: keep the type-shield technology decided by ocr-scan.
        const scanDryerTechnology: DryingTechnology | null = ocr.dryerTechnology ?? null;
        // An unreadable plate must not become an appliance with no brand,
        // model or type: fail the row so it is excluded and can be retried.
        if (!brand && !modelNumber && !productType) {
          throw new Error("Nothing readable on this type shield. Retake the photo or remove the row.");
        }
        // Map OCR productType ("Refrigerator", "Washing Machine", …) to a
        // company-configured category name so the UI shows the correct kind.
        const category: string =
          ocr.category ||
          matchCompanyCategory(productType, categoryNames) ||
          "";

        patchRow(row.id, {
          status: "enriching",
          brand,
          modelNumber,
          serialNumber,
          productNumber,
          productType,
          plateType,
          fdNumber,
          zNr,
          plateVersion,
          productionNumber,
          category,
          scanDryerTechnology,
        });

        let eprelReference: string | undefined;
        let energyClass: string | undefined;
        let productImageUrl: string | undefined;
        let eprelData: any | undefined;
        let eprelClassification: import("@/lib/eprelClassification").ApplianceClassification | undefined;
        if (modelNumber || brand) {
          const productGroup = inferProductGroup(productType, category);
          const sanitizeModel = (v: string) =>
            v.replace(/[^A-Za-z0-9\-\/\.\s_]/g, "").trim().slice(0, 50);
          const safeModel = sanitizeModel(modelNumber || brand);
          const safeProductNumber = productNumber ? sanitizeModel(productNumber) : undefined;
          const { data: eprelRes, error: eprelErr } = await supabase.functions.invoke("eprel-search", {
            body: {
              modelNumber: safeModel,
              brandName: brand || undefined,
              productNumber: safeProductNumber || undefined,
              productType: productType || undefined,
              productGroup,
            },
          });
          if (eprelErr) console.warn("Batch EPREL lookup failed", { modelNumber: safeModel, error: eprelErr.message });
          if (eprelRes?.success && eprelRes?.data) {
            eprelData = eprelRes.data;
            eprelReference = eprelRes.data.eprelRegistrationNumber;
            energyClass = eprelRes.data.energyClass || eprelRes.data.energyEfficiencyClass;
            productImageUrl = eprelRes.data.productImageUrl;
            eprelClassification = eprelRes.classification;
            // Fallback: derive locally if the edge function didn't return it
            if (!eprelClassification && eprelRes.productGroup) {
              const { mapEprelToClassification } = await import("@/lib/eprelClassification");
              eprelClassification = mapEprelToClassification(eprelRes.data, eprelRes.productGroup);
            }
          }
        }

        // Duplicate check
        let existingApplianceId: string | undefined;
        // FIX-304: no serial read → skip the row (not saved); otherwise match only by serial.
        const serialCandidates = companyId && normalizeSerial(serialNumber)
          ? await collectApplianceMatches({ brand, modelNumber, productNumber, serialNumber }, companyId)
          : [];
        const serialMatch = batchImportRowMatch({ brand, modelNumber, productNumber, serialNumber }, serialCandidates);
        if (serialMatch.skip) {
          patchRow(row.id, { status: "failed", error: i18n.t("common:scanMatch.batchNoSerial"), include: false });
          return;
        }
        existingApplianceId = serialMatch.existingApplianceId;

        patchRow(row.id, {
          status: "done",
          eprelReference,
          energyClass,
          productImageUrl,
          eprelData,
          eprelClassification,
          existingApplianceId,
        });

        // Harvest recommendations (non-blocking — row stays "done" regardless).
        // Use the canonical workbook category, derived from the EPREL
        // classification when available, otherwise from free text. The
        // workbook filter requires an EXACT match.
        const recCategory =
          eprelCategoryToWorkbookCategory(eprelClassification?.applianceCategory) ||
          freeTextToWorkbookCategory(category) ||
          freeTextToWorkbookCategory(productType);
        const recBrand = normalizeRecommendationBrand(brand);
        if (companyId && (recBrand || recCategory)) {
          patchRow(row.id, { recommendationsStatus: "loading" });
          // Technology is only meaningful for dryers in the workbook
          // (column E is blank for washers/dishwashers).
          const recTechnology =
            recCategory === "Tumble Dryer"
              ? eprelClassification?.dryerTechnology || ""
              : "";
          try {
            // Explicitly attach the current access token. Under concurrent
            // batch processing supabase-js occasionally cannot resolve the
            // session in time and falls back to the anon key, which the
            // edge function rejects as "Invalid token".
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData.session?.access_token;
            const { data: recData, error: recErr } = await supabase.functions.invoke(
              "harvest-recommendations",
              {
                body: { companyId, category: recCategory, technology: recTechnology, brand: recBrand },
                headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
              }
            );
            if (recErr) {
              const msg = (recErr as any)?.message || "";
              if (/No active recommendation sheet/i.test(msg)) {
                patchRow(row.id, { recommendationsStatus: "skipped", recommendations: [] });
              } else {
                patchRow(row.id, {
                  recommendationsStatus: "error",
                  recommendationsError: msg || "Recommendation failed",
                });
              }
            } else {
              patchRow(row.id, {
                recommendationsStatus: "done",
                recommendations: (recData?.items || []) as HarvestRecommendationItem[],
                recommendationsMatchedOn: recData?.matchedOn,
              });
            }
          } catch (e: any) {
            patchRow(row.id, {
              recommendationsStatus: "error",
              recommendationsError: e?.message || "Recommendation failed",
            });
          }
        }


      } catch (err: any) {
        console.error("Batch row failed:", err);
        patchRow(row.id, {
          status: "failed",
          error: err?.message || "Processing failed",
          include: false,
        });
      }
    },
    [companyId, categoryNames]
  );

  const startProcessing = async () => {
    if (rows.length === 0) return;
    setProcessing(true);
    setStep("review");
    const pending = rows.filter((r) => r.status === "pending" || r.status === "failed");
    await runWithLimit(pending, CONCURRENCY, (r) => processRow(r));
    setProcessing(false);
    toast.success("Scanning complete. Please review the results.");
  };

  const retryRow = async (row: Row) => {
    await processRow(row);
  };

  const removeSelected = () => {
    setRows((prev) => prev.filter((r) => !r.selected));
  };

  const setWorkflowForSelected = (wf: WorkflowKind) => {
    setRows((prev) => prev.map((r) => (r.selected ? { ...r, workflow: wf } : r)));
  };

  const includedRows = useMemo(
    () => rows.filter((r) => r.include && r.status === "done"),
    [rows]
  );
  const allSelected = rows.length > 0 && rows.every((r) => r.selected);
  const someSelected = rows.some((r) => r.selected);

  const finalize = async () => {
    if (!user || !companyId) {
      toast.error("No active company.");
      return;
    }
    if (includedRows.length === 0) {
      toast.error("No rows selected for creation.");
      return;
    }
    setFinalizing(true);
    let created = 0;
    let skipped = 0;
    for (const row of includedRows) {
      try {
        let applianceId = row.existingApplianceId;
        if (!applianceId) {
          const c = row.eprelClassification;
          const { data: ins, error } = await supabase
            .from("appliances")
            .insert({
              user_id: user.id,
              company_id: companyId,
              brand: row.brand || null,
              model_number: row.modelNumber,
              serial_number: row.serialNumber || null,
              product_number: row.productNumber || null,
              product_type: row.productType || null,
              plate_type: row.plateType || null,
              fd_number: row.fdNumber || null,
              z_nr: row.zNr || null,
              plate_version: row.plateVersion || null,
              production_number: row.productionNumber || null,
              category: row.category || null,
              name: [row.brand, row.modelNumber].filter(Boolean).join(" ") || row.modelNumber,
              type_shield_url: row.typeShieldUrl || null,
              image_url: row.productImageUrl || null,
              eprel_reference: row.eprelReference || null,
              eprel_data: row.eprelData || null,
              appliance_class: c?.applianceCategory || null,
              // FIX-289: type shield wins; EPREL only fills in when the scan found nothing.
              dryer_technology: resolveScannedDryingTechnology(row.scanDryerTechnology, c?.dryerTechnology ?? null),
              dishwasher_install: c?.dishwasherInstall || null,
              dishwasher_width: c?.dishwasherWidth || null,
              dishwasher_capacity: c?.dishwasherCapacity ?? null,
              classification_source: c ? c.classificationSource : null,
              status: "pending",
            } as any)
            .select("id")
            .single();
          if (error) throw error;
          applianceId = ins.id;
        } else {
          // FIX-289: a matched existing appliance also gets the scan's
          // drying technology and the washer dryer category fix.
          const { data: stored, error: readErr } = await supabase
            .from("appliances")
            .select("dryer_technology, category")
            .eq("id", applianceId)
            .eq("company_id", companyId)
            .maybeSingle();
          if (readErr) throw readErr;
          if (!stored) throw new Error("Existing appliance not found in this company.");
          const patch = existingAppliancePatch({
            stored,
            scanTechnology: row.scanDryerTechnology,
            eprelTechnology: row.eprelClassification?.dryerTechnology ?? null,
            productType: row.productType,
            categoryNames,
          });
          if (Object.keys(patch).length > 0) {
            const { data: upd, error: updErr } = await supabase
              .from("appliances")
              .update(patch as any)
              .eq("id", applianceId)
              .eq("company_id", companyId)
              .select("id");
            if (updErr) throw updErr;
            if (!upd || upd.length === 0) throw new Error("Existing appliance could not be updated.");
          }
        }

        const { error: woErr } = await supabase.from("work_orders").insert({
          appliance_id: applianceId!,
          user_id: user.id,
          company_id: companyId,
          assignee_id: user.id,
          type: row.workflow,
          status: "in_progress",
        });
        if (woErr) throw woErr;
        created++;
      } catch (err) {
        console.error("Finalize row failed", err);
        skipped++;
      }
    }
    setFinalizing(false);
    toast.success(`${created} work order(s) created${skipped ? `, ${skipped} skipped` : ""}.`);
    navigate("/work-orders");
  };

  const exportExcel = async () => {
    const exportRows = rows.filter((r) => r.include && r.status === "done");
    if (exportRows.length === 0) {
      toast.error("No rows to export.");
      return;
    }
    const workflowLabel: Record<WorkflowKind, string> = {
      repair: "Repair",
      refurbish: "Refurbishment",
      harvest: "Harvest",
    };
    const data = exportRows.map((r) => {
      const e = r.eprelData || {};
      const dims =
        e.height && e.width && e.depth ? `${e.height}×${e.width}×${e.depth} mm` : "";
      const capacity = e.ratedCapacity || e.capacity || "";
      // Prefer EPREL-resolved category over OCR/manual
      const category =
        e.applianceCategory || e.productGroup || e.category || r.category || "";
      const c = r.eprelClassification;
      return {
        "File name": r.file?.name || "",
        "Photo URL": r.typeShieldUrl || "",
        Brand: r.brand || "",
        "Model number": r.modelNumber || "",
        "Serial number": r.serialNumber || "",
        Category: category,
        "Appliance class": c?.applianceCategory
          ? APPLIANCE_CLASS_LABEL[c.applianceCategory] || c.applianceCategory
          : "",
        Technology:
          scanTechnologyLabel(r.scanDryerTechnology) ||
          ((c?.applianceCategory === "TUMBLE_DRYER" || c?.applianceCategory === "WASHER_DRYER") && c.dryerTechnology
            ? DRYER_TECH_LABEL[c.dryerTechnology] || ""
            : ""),
        "Installation type":
          c?.applianceCategory === "DISHWASHER" && c.dishwasherInstall
            ? DW_INSTALL_LABEL[c.dishwasherInstall] || ""
            : "",
        Width:
          c?.applianceCategory === "DISHWASHER" && c.dishwasherWidth
            ? DW_WIDTH_LABEL[c.dishwasherWidth] || ""
            : "",
        "Capacity (place settings)":
          c?.applianceCategory === "DISHWASHER" && c.dishwasherCapacity != null
            ? c.dishwasherCapacity
            : "",
        "Classification source": c?.classificationSource || "",
        "Energy class": r.energyClass || "",
        "EPREL reference": r.eprelReference || "",
        Dimensions: dims,
        Capacity: capacity,
        Workflow: workflowLabel[r.workflow],
        Duplicate: r.existingApplianceId ? "Yes" : "No",
        "Existing appliance ID": r.existingApplianceId || "",
        Status: r.status === "done" ? "Done" : "Failed",
        Error: r.error || "",
        "Recommended parts (count)":
          r.recommendationsStatus === "done" ? (r.recommendations?.length ?? 0) : "",
        "Recommended parts":
          r.recommendationsStatus === "done"
            ? (r.recommendations || []).map((x) => x.partName).join("; ")
            : "",
        "Recommended brand price total (€)":
          r.recommendationsStatus === "done"
            ? (r.recommendations || []).reduce((s, x) => s + (x.brandPrice ?? 0), 0)
            : "",
        "Recommended resale price total (€)":
          r.recommendationsStatus === "done"
            ? (r.recommendations || []).reduce((s, x) => s + (x.resalePrice ?? 0), 0)
            : "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const headers = Object.keys(data[0]);
    ws["!cols"] = headers.map((h) => ({
      wch: Math.min(
        50,
        Math.max(h.length, ...data.map((d) => String((d as any)[h] ?? "").length)) + 2
      ),
    }));
    ws["!freeze"] = { xSplit: 0, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Scanned devices");
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const fname = `type-shield-batch-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
      now.getDate()
    )}-${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    if (!user) {
      toast.error("Not signed in.");
      return;
    }
    setExporting(true);
    try {
      const path = `${user.id}/exports/${fname}`;
      const { error: upErr } = await supabase.storage
        .from("type-shield-images")
        .upload(path, blob, {
          contentType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          upsert: true,
        });
      if (upErr) {
        toast.error(`Export failed: ${upErr.message}`);
        return;
      }
      const { data: pub } = supabase.storage
        .from("type-shield-images")
        .getPublicUrl(path, { download: fname });
      setExportInfo({ url: pub.publicUrl, name: fname });
      toast.success(`Exported ${exportRows.length} row(s).`);
    } finally {
      setExporting(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const statusBadge = (r: Row) => {
    switch (r.status) {
      case "pending":
        return <Badge variant="secondary">Pending</Badge>;
      case "uploading":
        return (
          <Badge variant="secondary" className="gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Uploading
          </Badge>
        );
      case "scanning":
        return (
          <Badge variant="secondary" className="gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Scanning
          </Badge>
        );
      case "enriching":
        return (
          <Badge variant="secondary" className="gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Enriching
          </Badge>
        );
      case "done":
        return (
          <Badge className="gap-1 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
            <CheckCircle2 className="w-3 h-3" /> Done
          </Badge>
        );
      case "failed":
        return (
          <div className="space-y-1">
            <Badge variant="destructive" className="gap-1">
              <AlertCircle className="w-3 h-3" /> Failed
            </Badge>
            {r.error && <p className="text-xs text-destructive max-w-[14rem]">{r.error}</p>}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-4 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/work-orders")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Batch Type-Shield Import</h1>
          <p className="text-sm text-muted-foreground">
            Upload many type-shield photos, review the scanned results, then create work orders in bulk.
          </p>
        </div>
      </div>

      {step === "upload" && (
        <div className="px-6">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`border-2 border-dashed rounded-2xl p-10 text-center transition-colors ${
              dragOver ? "border-primary bg-primary/5" : "border-border bg-card"
            }`}
          >
            <ImagePlus className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium text-foreground mb-1">
              Drag &amp; drop type-shield images here
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              JPG, PNG or WEBP — up to {MAX_FILES} files per batch.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED.join(",")}
              className="hidden"
              onChange={(e) => e.target.files && addFiles(e.target.files)}
            />
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="w-4 h-4 mr-2" /> Choose files
            </Button>
          </div>

          {rows.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-muted-foreground">
                  {rows.length} / {MAX_FILES} selected
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setRows([])}>
                    Clear
                  </Button>
                  <Button onClick={startProcessing}>Start scanning</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {rows.map((r) => (
                  <div key={r.id} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                    <img src={r.previewUrl} alt={r.file.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeRow(r.id)}
                      className="absolute top-1 right-1 bg-background/90 rounded-full p-1 hover:bg-background"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step === "review" && (
        <div className="px-6">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(v) =>
                  setRows((prev) => prev.map((r) => ({ ...r, selected: Boolean(v) })))
                }
              />
              <span className="text-sm text-muted-foreground">
                {includedRows.length} of {rows.length} ready · {rows.filter((r) => r.status === "failed").length} failed
              </span>
              {someSelected && (
                <>
                  <Select onValueChange={(v) => setWorkflowForSelected(v as WorkflowKind)}>
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="Set workflow…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="repair">Repair</SelectItem>
                      <SelectItem value="refurbish">Refurbishment</SelectItem>
                      <SelectItem value="harvest">Harvest</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={removeSelected}>
                    <Trash2 className="w-4 h-4 mr-1" /> Remove
                  </Button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSkipConfirm(true)} disabled={finalizing}>
                Skip batch
              </Button>
              <Button
                variant="outline"
                onClick={exportExcel}
                disabled={processing || finalizing || exporting || includedRows.length === 0}
              >
                {exporting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</>
                ) : (
                  <><FileDown className="w-4 h-4 mr-2" /> Download Excel</>
                )}
              </Button>
              <Button onClick={finalize} disabled={processing || finalizing || includedRows.length === 0}>
                {finalizing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…
                  </>
                ) : (
                  <>Create {includedRows.length} work order(s)</>
                )}
              </Button>
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="p-2 w-10"></th>
                  <th className="p-2 w-20">Photo</th>
                  <th className="p-2 text-left">Brand</th>
                  <th className="p-2 text-left">Model</th>
                  <th className="p-2 text-left">Serial</th>
                  <th className="p-2 text-left">Category</th>
                  <th className="p-2 text-left">Classification</th>
                  <th className="p-2 text-left">EPREL</th>
                  <th className="p-2 text-left">Recommended parts</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Workflow</th>
                  <th className="p-2 text-center">Include</th>
                  <th className="p-2 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="p-2">
                      <Checkbox
                        checked={r.selected}
                        onCheckedChange={(v) => patchRow(r.id, { selected: Boolean(v) })}
                      />
                    </td>
                    <td className="p-2">
                      <button onClick={() => setZoomUrl(r.previewUrl)}>
                        <img
                          src={r.previewUrl}
                          alt=""
                          className="w-14 h-14 object-cover rounded border border-border"
                        />
                      </button>
                    </td>
                    <td className="p-2">
                      <Input
                        value={r.brand}
                        onChange={(e) => patchRow(r.id, { brand: e.target.value })}
                        className="h-8"
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        value={r.modelNumber}
                        onChange={(e) => patchRow(r.id, { modelNumber: e.target.value })}
                        className="h-8"
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        value={r.serialNumber}
                        onChange={(e) => patchRow(r.id, { serialNumber: e.target.value })}
                        className="h-8"
                      />
                    </td>
                    <td className="p-2">
                      <SearchableSelect
                        className="w-40"
                        size="sm"
                        value={r.category || null}
                        onChange={(v) => patchRow(r.id, { category: v ?? "" })}
                        placeholder="—"
                        searchPlaceholder="Search categories…"
                        emptyMessage="No categories found"
                        items={categoryNames.map((n) => ({ value: n, label: n }))}
                      />
                    </td>
                    <td className="p-2 text-xs">
                      {(() => {
                        const label =
                          scanTechnologyLabel(r.scanDryerTechnology) ||
                          formatClassification(r.eprelClassification);
                        if (label) return <div className="font-medium">{label}</div>;
                        if (r.status === "done" && !r.eprelClassification)
                          return <span className="text-muted-foreground">Manual review</span>;
                        return <span className="text-muted-foreground">—</span>;
                      })()}
                    </td>
                    <td className="p-2 text-xs">
                      {r.energyClass && <div className="font-medium">{r.energyClass}</div>}
                      {r.eprelReference && (
                        <div className="text-muted-foreground">#{r.eprelReference}</div>
                      )}
                    </td>
                    <td className="p-2 text-xs">
                      {(() => {
                        const s = r.recommendationsStatus || "idle";
                        if (s === "loading")
                          return (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <Loader2 className="w-3 h-3 animate-spin" /> Computing…
                            </span>
                          );
                        if (s === "skipped")
                          return <span className="text-muted-foreground">No sheet</span>;
                        if (s === "error")
                          return (
                            <span className="text-destructive">
                              {r.recommendationsError || "Error"}
                            </span>
                          );
                        if (s === "done") {
                          const items = r.recommendations || [];
                          if (items.length === 0)
                            return <span className="text-muted-foreground">No matches</span>;
                          return (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="underline decoration-dotted text-foreground hover:text-primary">
                                  {items.length} part{items.length === 1 ? "" : "s"}
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 max-h-80 overflow-y-auto">
                                <div className="text-xs font-medium mb-1">Recommended parts</div>
                                {r.recommendationsMatchedOn && (
                                  <div className="text-[10px] text-muted-foreground mb-2">
                                    Matched on: {r.recommendationsMatchedOn.category || "—"}
                                    {r.recommendationsMatchedOn.technology
                                      ? ` · ${r.recommendationsMatchedOn.technology}`
                                      : ""}
                                    {r.recommendationsMatchedOn.brand
                                      ? ` · ${r.recommendationsMatchedOn.brand}`
                                      : ""}
                                  </div>
                                )}
                                <ul className="space-y-1 text-xs">
                                  {items.map((it, i) => (
                                    <li key={i} className="flex justify-between gap-2">
                                      <span className="truncate">{it.partName}</span>
                                      <span className="text-muted-foreground whitespace-nowrap">
                                        {it.brandPrice != null ? `€${it.brandPrice.toFixed(0)}` : "—"}
                                        {" → "}
                                        {it.resalePrice != null ? `€${it.resalePrice.toFixed(0)}` : "—"}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </PopoverContent>
                            </Popover>
                          );
                        }
                        return <span className="text-muted-foreground">—</span>;
                      })()}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-col gap-1">
                        {statusBadge(r)}
                        {r.existingApplianceId && (
                          <Badge variant="outline" className="text-amber-700 border-amber-300">
                            Existing
                          </Badge>
                        )}
                        {r.error && (
                          <span className="text-xs text-destructive max-w-[180px]">{r.error}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-2">
                      <Select
                        value={r.workflow}
                        onValueChange={(v) => patchRow(r.id, { workflow: v as WorkflowKind })}
                      >
                        <SelectTrigger className="h-8 w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="repair">Repair</SelectItem>
                          <SelectItem value="refurbish">Refurbishment</SelectItem>
                          <SelectItem value="harvest">Harvest</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2 text-center">
                      <Checkbox
                        checked={r.include}
                        disabled={r.status !== "done"}
                        onCheckedChange={(v) => patchRow(r.id, { include: Boolean(v) })}
                      />
                    </td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        {r.status === "failed" && (
                          <Button size="icon" variant="ghost" onClick={() => retryRow(r)}>
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => removeRow(r.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={12} className="p-6 text-center text-muted-foreground">
                      No rows.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!zoomUrl} onOpenChange={(o) => !o && setZoomUrl(null)}>
        <DialogContent className="max-w-3xl">
          {zoomUrl && <img src={zoomUrl} alt="" className="w-full h-auto rounded" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!exportInfo} onOpenChange={(o) => !o && setExportInfo(null)}>
        <DialogContent className="max-w-md">
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Your Excel file is ready</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Download <span className="font-mono">{exportInfo?.name}</span> using the link below.
              </p>
            </div>
            {exportInfo && (
              <p className="text-sm break-all">
                <a
                  href={exportInfo.url}
                  download={exportInfo.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:opacity-80"
                >
                  {exportInfo.url}
                </a>
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={skipConfirm} onOpenChange={setSkipConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard batch?</AlertDialogTitle>
            <AlertDialogDescription>
              The scanned results will be discarded. Uploaded type-shield images remain in storage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                rows.forEach((r) => URL.revokeObjectURL(r.previewUrl));
                setRows([]);
                setStep("upload");
                setSkipConfirm(false);
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
